// IMPORTANT: Fabric.js configuration MUST be imported FIRST
// This sets FabricObject.customProperties before any Fabric objects are created
import './config/fabricConfig';

// Initialize debug logger (exposes window.canvasDebug)
import './utils/canvasDebugLogger';

// Self-hosted fonts (replaces the former Google Fonts <link> in index.html):
// the five below are the Direct Sale text-panel fonts (DIRECT_SALE_FONT_FACES
// in @designdeck/shared), bundled so end customers never call
// fonts.googleapis.com and CSP of embedding shops cannot block them. Only
// the weights in use.
import '@fontsource/archivo-black/400.css';
import '@fontsource/inter/800.css';
import '@fontsource/oswald/500.css';
import '@fontsource/playfair-display/700.css';
import '@fontsource/permanent-marker/400.css';
// The app's own type (see src/styles/theme.css): display, body and mono.
// Self-hosted for the same reason as above — the Direct Sale iframe shares
// this bundle.
import '@fontsource/funnel-display/400.css';
import '@fontsource/funnel-display/500.css';
import '@fontsource/funnel-display/600.css';
import '@fontsource/funnel-display/700.css';
import '@fontsource/funnel-display/800.css';
import '@fontsource/plus-jakarta-sans/300.css';
import '@fontsource/plus-jakarta-sans/400.css';
import '@fontsource/plus-jakarta-sans/500.css';
import '@fontsource/plus-jakarta-sans/600.css';
import '@fontsource/plus-jakarta-sans/700.css';
import '@fontsource/jetbrains-mono/400.css';
import '@fontsource/jetbrains-mono/500.css';
import '@fontsource/jetbrains-mono/600.css';
// The Seller Creator's text-tool fonts (config/fonts.ts FONTS): regular and
// bold, upright and italic — exactly what the bold/italic toggles offer. The
// weight files carry every subset with its unicode-range, so the browser only
// downloads the subsets a text actually uses. fonts.test.ts keeps this list
// and FONTS in step.
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/400-italic.css';
import '@fontsource/roboto/700.css';
import '@fontsource/roboto/700-italic.css';
import '@fontsource/open-sans/400.css';
import '@fontsource/open-sans/400-italic.css';
import '@fontsource/open-sans/700.css';
import '@fontsource/open-sans/700-italic.css';
import '@fontsource/lato/400.css';
import '@fontsource/lato/400-italic.css';
import '@fontsource/lato/700.css';
import '@fontsource/lato/700-italic.css';
import '@fontsource/montserrat/400.css';
import '@fontsource/montserrat/400-italic.css';
import '@fontsource/montserrat/700.css';
import '@fontsource/montserrat/700-italic.css';
import '@fontsource/poppins/400.css';
import '@fontsource/poppins/400-italic.css';
import '@fontsource/poppins/700.css';
import '@fontsource/poppins/700-italic.css';
import '@fontsource/oswald/400.css';
import '@fontsource/oswald/700.css';
import '@fontsource/raleway/400.css';
import '@fontsource/raleway/400-italic.css';
import '@fontsource/raleway/700.css';
import '@fontsource/raleway/700-italic.css';
import '@fontsource/ubuntu/400.css';
import '@fontsource/ubuntu/400-italic.css';
import '@fontsource/ubuntu/700.css';
import '@fontsource/ubuntu/700-italic.css';
import '@fontsource/playfair-display/400.css';
import '@fontsource/playfair-display/400-italic.css';
// playfair-display/700.css: imported with the Direct Sale fonts above
import '@fontsource/playfair-display/700-italic.css';
import '@fontsource/merriweather/400.css';
import '@fontsource/merriweather/400-italic.css';
import '@fontsource/merriweather/700.css';
import '@fontsource/merriweather/700-italic.css';

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import './index.css';
import { applyBranding } from '@/lib/theme';
import { applyEmbedTheme } from '@/lib/embed-theme';
import { applyEmbedLayout } from '@/lib/embed-layout';
import { initI18n } from '@/lib/i18n';
import { recordIframeParamsFromUrl } from '@/lib/iframe-boot';
import { markSessionStart } from '@/lib/session-start';

// A Seller iframe start carries ?lang= and the colour parameters next to the
// auto-login credentials, and the auto-login redirect drops them from the
// URL. Record them first, so the two steps below (and every later reload in
// this tab) can fall back to them.
recordIframeParamsFromUrl();
// Before any page renders: the overview compares publication times with it.
markSessionStart();

// An embedding theme (?embedTheme=polaris on a Seller iframe start) before the
// branding: under it the customer's colours do not apply.
applyEmbedTheme();
// Then the layout values on top of the theme's preset (?density=, ?radius=,
// ?tileMin=, ?chrome=; lib/embed-layout.ts).
applyEmbedLayout();

// Apply the embedder's colours from the iframe URL (?primaryColor=,
// ?paperColor=, ?inkColor=) before the app renders, so there is no flash of
// the default look. A customer's stored branding follows once it arrives
// (login, Direct Sale session) and merges underneath the URL values.
applyBranding();

// Same reasoning for the language (?lang=<code>): resolve it before the first
// render so the customer never sees the default language flash before theirs.
initI18n();

const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error('Root element not found');
}

createRoot(rootElement).render(
  <StrictMode>
    <App />
  </StrictMode>
);

// Enable HMR in development
if (import.meta.hot) {
  import.meta.hot.accept();
}
